import React, { memo, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Divider,
  Switch,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { format, parse, isValid } from 'date-fns';
import type { UrgentTarget, SlotUrgentSettingInput } from '../../../../domain/dailyShiftSlot';
import {
  IncentiveSettingPolicy,
  MIN_INCENTIVE_AMOUNT,
  MAX_INCENTIVE_AMOUNT,
  MIN_WORKING_HOURS,
  MAX_WORKING_HOURS,
} from '../../../../domain/dailyShiftSlot';
import { doctorApi } from '../../../../infrastructure/api/doctorApi';
import type { Doctor } from '../../../../infrastructure/api/doctorApi';

/**
 * 急募詳細ダイアログのProps
 */
export interface UrgentTargetDetailDialogProps {
  /** ダイアログの開閉状態 */
  open: boolean;
  /** 対象の急募枠 */
  target: UrgentTarget | null;
  /** 対象月の募集締切日時（ISO 8601・null=未設定）。通知文面の {募集締切} に使用 */
  deadlineAt: string | null;
  /** 処理中フラグ */
  isLoading: boolean;
  /** エラーメッセージ */
  error: string | null;
  /** エラークリア時のコールバック */
  onClearError: () => void;
  /** 直接確定時のコールバック */
  onConfirmDoctor: (doctorId: number) => Promise<boolean>;
  /** 急募キャンセル時のコールバック */
  onCancelUrgent: () => Promise<boolean>;
  /** 応募キャンセル時のコールバック（対象医師のシフトを削除する） */
  onCancelApplication: (doctorId: number) => Promise<boolean>;
  /** 急募設定保存時のコールバック */
  onSaveUrgentSettings: (settings: SlotUrgentSettingInput) => Promise<boolean>;
  /** 閉じる時のコールバック */
  onClose: () => void;
}

/**
 * 曜日名
 */
const DAY_OF_WEEK_NAMES = ['日', '月', '火', '水', '木', '金', '土'];

/**
 * 通知本文の最大文字数
 *
 * バックエンド（SaveSlotUrgentSettingsRequest の `max:1000`）と揃える。
 * 変数置換後の本文が送信されるため、判定も置換後の文字数で行う。
 */
const MAX_NOTIFICATION_MESSAGE_LENGTH = 1000;

/**
 * インセンティブ条件から当日最低勤務時間を読み戻すパターン
 *
 * バックエンドは `当日最低勤務時間: %d時間` の書式で incentive_conditions へ保存する
 * （SaveSlotUrgentSettingsUseCase / SaveUrgentSettingsUseCase の
 *  INCENTIVE_CONDITIONS_FORMAT）。書式外の値（旧データ・手入力）は読み取れないため既定値を使う。
 */
const MINIMUM_WORKING_HOURS_PATTERN = /当日最低勤務時間:\s*(\d+)\s*時間/;

/**
 * 当日最低勤務時間の既定値（インセンティブ条件から読み取れない場合）
 */
const DEFAULT_MINIMUM_WORKING_HOURS = '1';

/**
 * 通知文面に挿入できる変数
 *
 * ⚠ ここに並べるのは「この画面で実値へ置換できるもの」だけに限ること。
 * バックエンドは通知本文を一切加工せずそのまま LINE へ送信するため、
 * 置換できない変数を残すと `{診療科}` のような文字列がそのまま医師へ届く。
 * 急募枠のデータ（UrgentTarget）に診療科・応募URL・クリニック名は含まれないので扱わない。
 */
const NOTIFICATION_VARIABLES = ['{診療日時}', '{インセンティブ額}', '{募集締切}'] as const;

/**
 * 締切が未設定の場合に {募集締切} へ入る文字列
 */
const DEADLINE_UNSET_LABEL = '未設定';

/**
 * 診療日時ラベルを生成（YYYY/MM/DD（曜）HH:MM〜HH:MM）
 */
const formatTargetDateTime = (target: UrgentTarget): string => {
  const dateObj = parse(target.date, 'yyyy-MM-dd', new Date());
  const dateLabel = isValid(dateObj)
    ? `${target.date.replace(/-/g, '/')}（${DAY_OF_WEEK_NAMES[dateObj.getDay()]}）`
    : target.date;
  return `${dateLabel} ${target.startTime}〜${target.endTime}`;
};

/**
 * 応募日時ラベルを生成（MM/DD HH:mm）
 */
const formatAppliedAt = (appliedAt: string): string => {
  const dateObj = new Date(appliedAt);
  return isValid(dateObj) ? format(dateObj, 'MM/dd HH:mm') : appliedAt;
};

/**
 * 募集締切ラベルを生成（YYYY/MM/DD HH:mm）
 */
const formatDeadline = (deadlineAt: string | null): string => {
  if (!deadlineAt) {
    return DEADLINE_UNSET_LABEL;
  }
  const dateObj = new Date(deadlineAt);
  return isValid(dateObj) ? format(dateObj, 'yyyy/MM/dd HH:mm') : deadlineAt;
};

/**
 * インセンティブ額ラベルを生成（¥5,000）
 */
const formatIncentiveAmount = (amount: number): string => `¥${amount.toLocaleString()}`;

/**
 * インセンティブ条件から当日最低勤務時間を読み取る
 */
const extractMinimumWorkingHours = (incentiveConditions: string | null): string => {
  const matched = incentiveConditions?.match(MINIMUM_WORKING_HOURS_PATTERN);
  return matched ? matched[1] : DEFAULT_MINIMUM_WORKING_HOURS;
};

/**
 * LINE通知のデフォルト文面
 *
 * 変数は保存時にフロントエンドで実値へ置換される
 */
const DEFAULT_NOTIFICATION_MESSAGE = `【急募のお知らせ】

診療日：{診療日時}
インセンティブ：{インセンティブ額}

ご都合のつく先生はアプリから応募をお願いします。
締切：{募集締切}`;

/**
 * 通知文面の変数を実値へ置換する
 *
 * バックエンドは本文を加工せずそのまま送信するため、置換はここで完結させる。
 */
const replaceNotificationVariables = (
  message: string,
  target: UrgentTarget,
  incentiveAmount: number,
  deadlineAt: string | null
): string =>
  message
    .replace(/\{診療日時\}/g, formatTargetDateTime(target))
    .replace(/\{インセンティブ額\}/g, formatIncentiveAmount(incentiveAmount))
    .replace(/\{募集締切\}/g, formatDeadline(deadlineAt));

/**
 * 急募設定フォームの状態
 */
interface UrgentSettingsFormState {
  /** インセンティブ金額（円）※入力途中の空欄を許容するため文字列で保持 */
  incentiveAmount: string;
  /** 当日最低勤務時間（時間）※入力途中の空欄を許容するため文字列で保持 */
  minimumWorkingHours: string;
  /** 保存時にLINE通知を送信するか */
  sendLineNotification: boolean;
  /** LINE通知文面（変数は保存時に実値へ置換） */
  notificationMessage: string;
}

/**
 * 急募設定フォームのエラー状態
 */
interface UrgentSettingsFormErrors {
  incentiveAmount?: string;
  minimumWorkingHours?: string;
  notificationMessage?: string;
}

/**
 * 急募詳細ダイアログコンポーネント
 *
 * - タイトル: 診療日時（診療科は表示しない）＋ステータス（募集中/確定済み）
 * - 応募状況（先着順）: 応募一覧を確定/補欠チップ付きで表示。「キャンセル」で対象医師のシフトを削除
 * - 急募設定: インセンティブ金額・当日最低勤務時間・LINE通知（送信トグル・文面編集・変数挿入）
 * - 応募外の医師から直接指定: 契約済み医師を氏名で絞り込み、「直接確定」で確定
 * - 「急募をキャンセルする」: 対象枠の急募医師枠数を0に更新
 */
const UrgentTargetDetailDialog: React.FC<UrgentTargetDetailDialogProps> = memo(({
  open,
  target,
  deadlineAt,
  isLoading,
  error,
  onClearError,
  onConfirmDoctor,
  onCancelUrgent,
  onCancelApplication,
  onSaveUrgentSettings,
  onClose,
}) => {
  // 医師一覧の状態
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [doctorsError, setDoctorsError] = useState<string | null>(null);

  // 医師名絞り込みテキスト
  const [searchText, setSearchText] = useState('');

  // 直接確定の確認ダイアログ対象医師
  const [confirmDoctor, setConfirmDoctor] = useState<Doctor | null>(null);

  // 急募キャンセルの確認ダイアログ表示状態
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // 応募キャンセルの確認ダイアログ対象（rank と氏名を確認文に出すため応募をそのまま保持）
  const [cancelApplicationTarget, setCancelApplicationTarget] = useState<
    UrgentTarget['applications'][number] | null
  >(null);

  // 急募設定フォームの状態
  const [formState, setFormState] = useState<UrgentSettingsFormState>({
    incentiveAmount: '0',
    minimumWorkingHours: DEFAULT_MINIMUM_WORKING_HOURS,
    sendLineNotification: false,
    notificationMessage: DEFAULT_NOTIFICATION_MESSAGE,
  });
  const [formErrors, setFormErrors] = useState<UrgentSettingsFormErrors>({});
  const [saveCompleted, setSaveCompleted] = useState(false);

  // 通知文面入力欄への参照（変数挿入時のカーソル位置取得用）
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);

  const targetId = target?.id ?? null;

  // 契約済み医師一覧を取得（ダイアログオープン時）
  useEffect(() => {
    if (open) {
      let cancelled = false;
      setLoadingDoctors(true);
      setDoctorsError(null);
      doctorApi
        .fetchDoctors({ status: '契約済み' })
        .then((result) => {
          if (!cancelled) {
            setDoctors(result);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setDoctorsError(err instanceof Error ? err.message : '医師一覧の取得に失敗しました');
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoadingDoctors(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }
    // クローズ時に入力状態をリセット
    setSearchText('');
    setConfirmDoctor(null);
    setShowCancelConfirm(false);
    setCancelApplicationTarget(null);
    setSaveCompleted(false);
    setFormErrors({});
    return undefined;
  }, [open]);

  /**
   * 急募設定フォームを対象枠の保存値で初期化する
   *
   * 依存は枠ID。保存やキャンセルの後は一覧の再取得で target の参照が変わるが、
   * 同じ枠を見ている間は入力中の値を保持したいため ID で判定する。
   */
  useEffect(() => {
    if (!open || !target) {
      return;
    }
    setFormState({
      incentiveAmount: String(target.incentive ?? 0),
      minimumWorkingHours: extractMinimumWorkingHours(target.incentiveConditions),
      // 既定はオフ。開くたびに送信予定の状態にはしない（誤送信を避けるため）
      sendLineNotification: false,
      notificationMessage: DEFAULT_NOTIFICATION_MESSAGE,
    });
    setFormErrors({});
    setSaveCompleted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetId]);

  /**
   * 応募一覧（先着順にソート）
   */
  const applications = useMemo(() => {
    if (!target) {
      return [];
    }
    return [...target.applications].sort((a, b) => a.rank - b.rank);
  }, [target]);

  /**
   * 医師名で絞り込んだ医師一覧
   */
  const filteredDoctors = useMemo(() => {
    const keyword = searchText.trim();
    if (!keyword) {
      return doctors;
    }
    return doctors.filter((doctor) =>
      `${doctor.lastName} ${doctor.firstName}`.includes(keyword) ||
      `${doctor.lastName}${doctor.firstName}`.includes(keyword)
    );
  }, [doctors, searchText]);

  /**
   * 変数置換後の通知本文（プレビューと送信で同じ値を使う）
   */
  const resolvedNotificationMessage = useMemo(() => {
    if (!target) {
      return '';
    }
    return replaceNotificationVariables(
      formState.notificationMessage,
      target,
      Number(formState.incentiveAmount || 0),
      deadlineAt
    );
  }, [formState.notificationMessage, formState.incentiveAmount, target, deadlineAt]);

  /**
   * インセンティブ金額変更ハンドラー
   */
  const handleIncentiveAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // 数字以外を除去し、先頭ゼロも自動除去する（"0" 単体・空欄は許容）
      const value = event.target.value
        .replace(/[^0-9]/g, '')
        .replace(/^0+(?=\d)/, '');
      setFormState((prev) => ({ ...prev, incentiveAmount: value }));
      setSaveCompleted(false);
    },
    []
  );

  /**
   * 当日最低勤務時間変更ハンドラー
   */
  const handleWorkingHoursChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
        .replace(/[^0-9]/g, '')
        .replace(/^0+(?=\d)/, '');
      setFormState((prev) => ({ ...prev, minimumWorkingHours: value }));
      setSaveCompleted(false);
    },
    []
  );

  /**
   * LINE通知送信トグル変更ハンドラー
   */
  const handleSendLineNotificationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormState((prev) => ({ ...prev, sendLineNotification: event.target.checked }));
      setSaveCompleted(false);
    },
    []
  );

  /**
   * 通知文面変更ハンドラー
   */
  const handleNotificationMessageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormState((prev) => ({ ...prev, notificationMessage: event.target.value }));
      setFormErrors((prev) => ({ ...prev, notificationMessage: undefined }));
      setSaveCompleted(false);
    },
    []
  );

  /**
   * 変数チップクリックハンドラー
   * 通知文面のカーソル位置に変数を挿入する
   */
  const handleInsertVariable = useCallback((variable: string) => {
    const input = messageInputRef.current;
    setFormState((prev) => {
      const message = prev.notificationMessage;
      const start = input?.selectionStart ?? message.length;
      const end = input?.selectionEnd ?? message.length;
      return {
        ...prev,
        notificationMessage: `${message.slice(0, start)}${variable}${message.slice(end)}`,
      };
    });
    // 挿入した変数の直後にカーソルを移動
    if (input) {
      const caretPosition = (input.selectionStart ?? input.value.length) + variable.length;
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(caretPosition, caretPosition);
      });
    }
    setSaveCompleted(false);
  }, []);

  /**
   * 急募設定の保存ハンドラー
   *
   * 通知本文は変数置換後の文字数で上限判定する（送信されるのは置換後の本文のため）
   */
  const handleSaveUrgentSettings = useCallback(async () => {
    const errors: UrgentSettingsFormErrors = {};

    const incentiveAmountValue =
      formState.incentiveAmount === '' ? NaN : Number(formState.incentiveAmount);
    if (!IncentiveSettingPolicy.isValidIncentiveAmount(incentiveAmountValue)) {
      errors.incentiveAmount = `インセンティブ金額は${MIN_INCENTIVE_AMOUNT}〜${MAX_INCENTIVE_AMOUNT}円の整数で入力してください`;
    }

    const workingHoursValue =
      formState.minimumWorkingHours === '' ? NaN : Number(formState.minimumWorkingHours);
    if (!IncentiveSettingPolicy.isValidWorkingHours(workingHoursValue)) {
      errors.minimumWorkingHours = `勤務時間は${MIN_WORKING_HOURS}〜${MAX_WORKING_HOURS}時間の整数で入力してください`;
    }

    if (resolvedNotificationMessage.length > MAX_NOTIFICATION_MESSAGE_LENGTH) {
      errors.notificationMessage = `通知文面は変数置換後で${MAX_NOTIFICATION_MESSAGE_LENGTH}文字以内にしてください（現在 ${resolvedNotificationMessage.length} 文字）`;
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const saved = await onSaveUrgentSettings({
      incentiveAmount: incentiveAmountValue,
      minimumWorkingHours: workingHoursValue,
      sendLineNotification: formState.sendLineNotification,
      notificationMessage: resolvedNotificationMessage,
    });

    if (saved) {
      setSaveCompleted(true);
      // 保存できた通知は再送信されないよう、トグルを既定のオフへ戻す
      setFormState((prev) => ({ ...prev, sendLineNotification: false }));
    }
  }, [formState, resolvedNotificationMessage, onSaveUrgentSettings]);

  /**
   * 直接確定の実行ハンドラー
   */
  const handleConfirmDoctorExecute = useCallback(async () => {
    if (!confirmDoctor) {
      return;
    }
    await onConfirmDoctor(confirmDoctor.id);
    setConfirmDoctor(null);
  }, [confirmDoctor, onConfirmDoctor]);

  /**
   * 急募キャンセルの実行ハンドラー
   */
  const handleCancelUrgentExecute = useCallback(async () => {
    await onCancelUrgent();
    setShowCancelConfirm(false);
  }, [onCancelUrgent]);

  /**
   * 応募キャンセルの実行ハンドラー
   */
  const handleCancelApplicationExecute = useCallback(async () => {
    if (!cancelApplicationTarget) {
      return;
    }
    await onCancelApplication(cancelApplicationTarget.doctorId);
    setCancelApplicationTarget(null);
  }, [cancelApplicationTarget, onCancelApplication]);

  if (!target) {
    return null;
  }

  // 確定数が急募枠数以上なら確定済みとみなす
  const isSlotConfirmed = target.urgentSlots > 0 && target.confirmedSlots >= target.urgentSlots;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={600} component="span">
                {formatTargetDateTime(target)}
              </Typography>
              <Chip
                label={isSlotConfirmed ? '確定済み' : '募集中'}
                color={isSlotConfirmed ? 'primary' : 'success'}
                size="small"
              />
            </Box>
            <Button
              onClick={onClose}
              size="small"
              sx={{ minWidth: 'auto' }}
            >
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* エラー表示 */}
          {error && (
            <Alert severity="error" onClose={onClearError} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 応募状況（先着順） */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            応募状況（先着順）
          </Typography>
          {applications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              応募データはまだありません
            </Typography>
          ) : (
            <Box>
              {applications.map((application) => (
                <Box
                  key={`${application.rank}-${application.doctorId}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 36 }}>
                    {application.rank}着
                  </Typography>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2">
                      {application.doctorName} 先生
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      応募 {formatAppliedAt(application.appliedAt)}
                    </Typography>
                  </Box>
                  <Chip
                    label={application.status}
                    color={application.status === '確定' ? 'success' : 'default'}
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => setCancelApplicationTarget(application)}
                    disabled={isLoading}
                  >
                    キャンセル
                  </Button>
                </Box>
              ))}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            募集枠が埋まり次第、応募順に自動で確定されます。
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* 急募設定 */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            急募設定
          </Typography>
          {saveCompleted && (
            <Alert severity="success" onClose={() => setSaveCompleted(false)} sx={{ mb: 1 }}>
              急募設定を保存しました
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="インセンティブ金額（円）"
              type="text"
              value={formState.incentiveAmount}
              onChange={handleIncentiveAmountChange}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              placeholder={`${MIN_INCENTIVE_AMOUNT}〜${MAX_INCENTIVE_AMOUNT}`}
              error={!!formErrors.incentiveAmount}
              helperText={formErrors.incentiveAmount}
              disabled={isLoading}
              fullWidth
              size="small"
            />
            <TextField
              label="当日最低勤務時間（時間）"
              type="text"
              value={formState.minimumWorkingHours}
              onChange={handleWorkingHoursChange}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              placeholder={`${MIN_WORKING_HOURS}〜${MAX_WORKING_HOURS}`}
              error={!!formErrors.minimumWorkingHours}
              helperText={formErrors.minimumWorkingHours}
              disabled={isLoading}
              fullWidth
              size="small"
            />

            {/* LINE通知 */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  保存時にLINE通知を送信する
                </Typography>
                <Switch
                  checked={formState.sendLineNotification}
                  onChange={handleSendLineNotificationChange}
                  disabled={isLoading}
                />
              </Box>
              <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip label="LINE" color="success" size="small" />
                  <Typography variant="caption" color="text.secondary">
                    通知文面（クリックして編集）
                  </Typography>
                </Box>
                <TextField
                  multiline
                  minRows={6}
                  fullWidth
                  value={formState.notificationMessage}
                  onChange={handleNotificationMessageChange}
                  inputRef={messageInputRef}
                  error={!!formErrors.notificationMessage}
                  helperText={formErrors.notificationMessage}
                  disabled={isLoading}
                  sx={{ bgcolor: 'background.paper' }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  {NOTIFICATION_VARIABLES.map((variable) => (
                    <Chip
                      key={variable}
                      label={variable}
                      size="small"
                      variant="outlined"
                      onClick={() => handleInsertVariable(variable)}
                      disabled={isLoading}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  ↑ 変数をクリックするとカーソル位置に挿入されます
                </Typography>

                {/* 送信内容プレビュー（変数置換後・実際に送信される本文） */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  送信される本文（変数置換後・{resolvedNotificationMessage.length}/{MAX_NOTIFICATION_MESSAGE_LENGTH}文字）
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    mt: 0.5,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  <Typography variant="body2" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {resolvedNotificationMessage}
                  </Typography>
                </Box>
                {!deadlineAt && formState.notificationMessage.includes('{募集締切}') && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    この月の募集締切が未設定のため、{'{募集締切}'} は「{DEADLINE_UNSET_LABEL}」として送信されます。
                  </Alert>
                )}
              </Box>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveUrgentSettings}
              disabled={isLoading}
              fullWidth
            >
              {isLoading
                ? '処理中...'
                : formState.sendLineNotification
                  ? '保存してLINE通知を送信'
                  : '急募設定を保存'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              保存しても急募医師枠数は変更されません。枠数を0にする場合は下の「急募をキャンセルする」を使用してください。
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* 応募外の医師から直接指定 */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            応募外の医師から直接指定
          </Typography>
          <TextField
            placeholder="医師名で絞り込む…"
            size="small"
            fullWidth
            value={searchText}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchText(event.target.value)}
            sx={{ mb: 1 }}
          />
          {doctorsError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {doctorsError}
            </Alert>
          )}
          {loadingDoctors ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List
              dense
              sx={{
                maxHeight: 240,
                overflow: 'auto',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                py: 0,
              }}
            >
              {filteredDoctors.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="body2" color="text.secondary">
                        該当する医師がいません
                      </Typography>
                    }
                  />
                </ListItem>
              ) : (
                filteredDoctors.map((doctor) => (
                  <ListItem
                    key={doctor.id}
                    divider
                    secondaryAction={
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => setConfirmDoctor(doctor)}
                        disabled={isLoading}
                      >
                        直接確定
                      </Button>
                    }
                  >
                    <ListItemText primary={`${doctor.lastName} ${doctor.firstName} 先生`} />
                  </ListItem>
                ))
              )}
            </List>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            直接指定した場合、応募中の先生より優先して確定されます。
            {target.incentive != null
              ? `インセンティブ（¥${target.incentive.toLocaleString()}）は同額適用。`
              : 'インセンティブは同額適用。'}
          </Typography>

          {/* 急募をキャンセルする */}
          <Button
            variant="outlined"
            color="error"
            fullWidth
            sx={{ mt: 3 }}
            onClick={() => setShowCancelConfirm(true)}
            disabled={isLoading}
          >
            急募をキャンセルする
          </Button>
        </DialogContent>
      </Dialog>

      {/* 直接確定の確認ダイアログ */}
      <Dialog
        open={confirmDoctor !== null}
        onClose={() => setConfirmDoctor(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            確認
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDoctor
              ? `${confirmDoctor.lastName} ${confirmDoctor.firstName} 先生をこの急募枠で直接確定します。よろしいですか？`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmDoctor(null)}
            disabled={isLoading}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmDoctorExecute}
            disabled={isLoading}
          >
            {isLoading ? '処理中...' : '確定する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 応募キャンセルの確認ダイアログ */}
      <Dialog
        open={cancelApplicationTarget !== null}
        onClose={() => setCancelApplicationTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            応募のキャンセル
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {cancelApplicationTarget
              ? `${cancelApplicationTarget.doctorName} 先生（${cancelApplicationTarget.rank}着）の応募を取り消し、この枠の医師シフトを削除します。よろしいですか？`
              : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            削除後、募集済み枠数が再集計されます。この操作は画面からは元に戻せません。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="outlined"
            onClick={() => setCancelApplicationTarget(null)}
            disabled={isLoading}
          >
            戻る
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelApplicationExecute}
            disabled={isLoading}
          >
            {isLoading ? '処理中...' : '応募を取り消す'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 急募キャンセルの確認ダイアログ */}
      <Dialog
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            確認
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            この急募をキャンセルします。対象枠の急募医師枠数が0に更新されます。よろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="outlined"
            onClick={() => setShowCancelConfirm(false)}
            disabled={isLoading}
          >
            戻る
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelUrgentExecute}
            disabled={isLoading}
          >
            {isLoading ? '処理中...' : 'キャンセルする'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

UrgentTargetDetailDialog.displayName = 'UrgentTargetDetailDialog';

export default UrgentTargetDetailDialog;
